import React, { FC, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';
import { Order, ProductModel, ProductVisibility } from '../../shared/orders';
import { TranslationKey, useTranslation } from '../translations';
import { displayName, Fetcher, isResourceError, useFetcher, useUserSafe } from '../utils';
import { captureError } from '../utils/errorHandling';
import { LinkButton, MaterialIcon, RelatedProduct } from '.';

export interface SecondaryAction {
  text: TranslationKey;
  readonly to?: string;
  readonly onClick?: (product: ProductModel) => void;
}

interface PageTextProps {
  readonly id: string;
  readonly title: string;
  readonly subtext: React.JSX.Element;
  readonly secondaryAction?: SecondaryAction;
  readonly orderComplete?: boolean;
}

interface AddedToCartProps extends PageTextProps {
  readonly product: ProductModel;
}

export const AddedToCart: FC<AddedToCartProps> = ({
  product,
  id,
  title,
  subtext,
  secondaryAction,
  orderComplete,
}) => {
  const { ts } = useTranslation();
  const relatedProducts = product.addons.filter(
    ({ isFree, visibility }) => !isFree && visibility === ProductVisibility.PUBLIC,
  );

  const onClick = useCallback(
    () => secondaryAction?.onClick?.(product),
    [secondaryAction, product],
  );

  return (
    <Row className="justify-content-center" id={id}>
      <Col className="mb-10" id="addedToCart" lg={10} xs={12}>
        <Card className="mb-10">
          <CardBody className="text-center">
            <MaterialIcon large name="check_circle" type="success" />
            <h3 className="text-success">{title}</h3>
            {subtext}
            <Row className="justify-content-center">
              {!orderComplete && (
                <Col className="mb-10" md={3} xs={12}>
                  <Link className="button-link" to="/cart">
                    <Button block color="primary" id="viewCart">
                      {ts('added_to_cart.view_cart')}
                    </Button>
                  </Link>
                </Col>
              )}
              {secondaryAction?.onClick && (
                <Col className="mb-10" md={3} xs={12}>
                  <Button block color="secondary" onClick={onClick} outline>
                    {ts(secondaryAction.text)}
                  </Button>
                </Col>
              )}
              {secondaryAction?.to && (
                <Col className="mb-10" md={3} xs={12}>
                  <LinkButton block color="secondary" outline to={secondaryAction.to}>
                    {ts(secondaryAction.text)}
                  </LinkButton>
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>
      </Col>
      {relatedProducts.length > 0 && (
        <Col id="relatedProducts" lg={10} xs={12}>
          <Card>
            <CardBody>
              <h4>{ts('added_to_cart.product_addons')}</h4>
              <hr />
              <Row>
                {relatedProducts.map((addon) => (
                  <RelatedProduct addon={addon} key={addon.id} />
                ))}
              </Row>
            </CardBody>
          </Card>
        </Col>
      )}
    </Row>
  );
};

interface AddedToCartCheckProps {
  readonly product: ProductModel;
  readonly secondaryAction?: SecondaryAction;
}

export const AddedToCartCheck: FC<AddedToCartCheckProps> = ({ product, secondaryAction }) => {
  const { ts } = useTranslation();
  const user = useUserSafe();

  const cartState = useFetcher<Order | undefined>(async () => {
    // Check to see if an order exists
    try {
      return await api.getActiveOrder(user.id, false);
    } catch (error) {
      if (!isResourceError(error, 'Order')) {
        // If it's not an Order ResourceError, then there's a bigger issue
        captureError(error as Error);
      }
    }

    return undefined;
  }, [user.id]);

  if (!cartState.success) {
    return <Fetcher result={cartState} />;
  }

  const order = cartState.data;

  if (!order || order.orderItems.length === 0) {
    return <AddedToCartOrderComplete product={product} />;
  }

  return (
    <AddedToCart
      id="productAddedToCart"
      product={product}
      secondaryAction={secondaryAction}
      subtext={<p>{ts('added_to_cart.named_product_added_to_your_cart', displayName(product))}</p>}
      title={ts('added_to_cart.product_added_to_cart')}
    />
  );
};

interface AddedToCartOrderCompleteProps {
  readonly product: ProductModel;
}

export const AddedToCartOrderComplete: FC<AddedToCartOrderCompleteProps> = ({ product }) => {
  const { ts } = useTranslation();
  const history = useHistory();

  return (
    <AddedToCart
      id="productAddedToCart"
      orderComplete
      product={product}
      secondaryAction={{
        onClick: () => {
          history.push('/account/orders');
        },
        text: 'added_to_cart.view_order_history',
      }}
      subtext={
        <>
          <p>{ts('added_to_cart.subtext.completed', displayName(product))}</p>
          <p>{ts('added_to_cart.subtext.history')}</p>
        </>
      }
      title={ts('added_to_cart.order_completed')}
    />
  );
};
